import { ChangeEvent } from 'react';
import { Box, Card, Text, Toggle } from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import { PromotionTypes } from '@tiendanube/common/src/domains/marketing/promotions/PromotionTypes';
import { ErrorScreen } from 'commons/components';
import { useAutoFocus } from 'commons/hooks';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import { useGetThemesConfigQuery } from 'domains/Online/Themes/themesApi';
import CustomLabelSkeleton from './CustomLabelSkeleton';
import { PromotionFormProps } from '../../../PromotionForm';
import { ErrorBox } from '../ErrorBox';

type CustomLabelCardProps = Pick<
  PromotionFormProps,
  'values' | 'onChange' | 'setFieldValue' | 'errors'
>;

const VALID_CUSTOM_LABEL_REGEX = /^(?!\s*$)(?!.*<[^>]*>)[^\r\n]*$/;

export function CustomLabelCard({
  values: { customLabel, customLabelEnabled, promotionType },
  onChange,
  setFieldValue,
  errors,
}: Readonly<CustomLabelCardProps>) {
  const t = useTranslationMarketing('promotions.settingsPage.customLabel');

  const {
    data: themesConfig,
    isLoading,
    isError,
    refetch,
  } = useGetThemesConfigQuery();

  const infoTexts = {
    [PromotionTypes.BUY_X_PAY_Y]: t('infoBuyXPayY'),
    [PromotionTypes.PROGRESSIVE_DISCOUNT]: t('infoProgressiveDiscount'),
    [PromotionTypes.BUY_2_PAY_1]: t('infoBuyXPayY'),
    [PromotionTypes.BUY_3_PAY_2]: t('infoBuyXPayY'),
    [PromotionTypes.CROSS_SELLING]: t('infoPercentageOff'),
    [PromotionTypes.PERCENTAGE_OFF]: t('infoPercentageOff'),
  };

  const infoText = infoTexts[promotionType]
    ? `${t('info')} ${infoTexts[promotionType]}`
    : t('info');

  const { ref } = useAutoFocus();

  if (isLoading) return <CustomLabelSkeleton />;

  const themeName = themesConfig?.currentTheme?.name?.toLowerCase();
  if (themeName === 'patagonia') return null;

  const handleToggle = () => {
    setFieldValue('customLabelEnabled', !customLabelEnabled);
    if (customLabelEnabled) {
      setFieldValue('customLabel', '');
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (VALID_CUSTOM_LABEL_REGEX.test(newValue) || newValue === '') {
      setFieldValue('customLabel', newValue);
      onChange(e);
    }
  };

  const customLabelError = errors?.customLabel;

  return (
    <Card>
      <Box display="flex" justifyContent="space-between" marginTop="1">
        <Card.Header title={t('title')} />
        {!isError && (
          <Toggle
            name="customLabelToggle"
            checked={customLabelEnabled}
            onChange={handleToggle}
          />
        )}
      </Box>
      {isError ? (
        <Card.Body>
          <ErrorScreen onRetry={refetch} />
        </Card.Body>
      ) : (
        customLabelEnabled && (
          <Card.Body>
            <Box
              display="flex"
              flexDirection="column"
              gap="3"
              marginBottom="1"
              ref={ref}
            >
              <Text>{infoText}</Text>
              <FormField.Input
                label={t('label')}
                name="customLabel"
                value={customLabel ?? ''}
                onChange={handleChange}
                showHelpText
                helpText={`${customLabel?.length ?? 0}/30 ${t('characters')}`}
                appearance={customLabelError ? 'danger' : undefined}
              />
              {!!customLabelError && (
                <ErrorBox errorMessage={t(customLabelError)} />
              )}
            </Box>
          </Card.Body>
        )
      )}
    </Card>
  );
}
